<template>
  <div class="fullscreen-page login">
    <div class="card auth">
      <h1>Eating Time Tracker</h1>
      <p class="subtitle">Track fasting intervals with your groups</p>

      <div v-if="authMode === 'login'" class="auth-form">
        <h2>Login</h2>
        <form @submit.prevent="handleLogin">
          <div class="form-group">
            <label for="username">Username:</label>
            <input
              id="username"
              v-model="form.username"
              type="text"
              placeholder="Enter your username"
              required
            />
          </div>

          <div class="form-group">
            <label for="password">Password:</label>
            <input
              id="password"
              v-model="form.password"
              type="password"
              placeholder="Enter your password"
              required
            />
          </div>

          <div v-if="error" class="error-message">{{ error }}</div>

          <button type="submit" class="primary btn" :disabled="loading">
            {{ loading ? 'Logging in...' : 'Login' }}
          </button>
        </form>

        <div v-if="enableRegistration" class="auth-switch">
          <p>Don't have an account?</p>
          <button @click="authMode = 'register'" class="link-button">
            Create one
          </button>
        </div>
      </div>

      <div v-else-if="authMode === 'register'" class="auth-form">
        <h2>Register</h2>
        <form @submit.prevent="handleRegister">
          <div class="form-group">
            <label for="reg-username">Username:</label>
            <input
              id="reg-username"
              v-model="form.username"
              type="text"
              placeholder="Choose a username (min 3 chars)"
              required
            />
          </div>

          <div class="form-group">
            <label for="reg-password">Password:</label>
            <input
              id="reg-password"
              v-model="form.password"
              type="password"
              placeholder="Choose a password (min 6 chars)"
              required
            />
          </div>

          <div v-if="error" class="error-message">{{ error }}</div>

          <button type="submit" class="primary btn" :disabled="loading">
            {{ loading ? 'Registering...' : 'Register' }}
          </button>
        </form>

        <div class="auth-switch">
          <p>Already have an account?</p>
          <button @click="authMode = 'login'" class="link-button">
            Sign in
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '../composables/useAuth'
import { API } from '../api/client'

const router = useRouter()
const { login, register } = useAuth()

const authMode = ref<'login' | 'register'>('login')
const enableRegistration = ref(false)
const loading = ref(false)
const error = ref('')

const form = ref({
  username: '',
  password: '',
})

const emit = defineEmits<{
  authenticated: []
}>()

onMounted(async () => {
  try {
    const status = await API.getRegistrationStatus()
    enableRegistration.value = status?.available ?? false
  } catch (err) {
    enableRegistration.value = false
  }
})

async function handleLogin() {
  loading.value = true
  error.value = ''

  try {
    await login(form.value.username, form.value.password)
    await router.push('/')
  } catch (err) {
    error.value =
      err instanceof Error ? err.message : 'Login failed. Please try again.'
  } finally {
    loading.value = false
  }
}

async function handleRegister() {
  loading.value = true
  error.value = ''

  try {
    await register(form.value.username, form.value.password)
    await router.push('/')
  } catch (err) {
    error.value =
      err instanceof Error ? err.message : 'Registration failed. Please try again.'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.login.fullscreen-page  {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--background) 0%, var(--background-accent) 100%);
  padding: 1rem;
}

.card.auth {
	border-radius: 0.5em;
	padding: 2rem;
	width: 100%;
	max-width: 400px;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
	& h1 {
	margin: 0 0 0.5rem;
	font-size: 1.8rem;
	/* color: #333; */
	text-align: center;
	}
}

.subtitle {
  margin: 0 0 2rem;
  /* color: #666; */
  font-size: 0.95rem;
  text-align: center;
}

.auth-form h2 {
  margin: 0 0 1.5rem;
  font-size: 1.3rem;
  /* color: #333; */
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.form-group label {
  font-weight: 500;
  /* color: #555; */
  font-size: 0.9rem;
}

.form-group input {
  padding: 0.75rem;
  border: 1px solid var(--theme-color-4);
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
}

.form-group input:focus {
  outline: none;
  border-color: var(--theme-color-2);
  box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
}

.error-message {
  background-color: #fee;
  color: #c00;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.auth-switch {
  margin-top: 1.5rem;
  text-align: center;
}

.auth-switch p {
  margin: 0 0 0.5rem;
  color: #666;
  font-size: 0.9rem;
}

.link-button {
	background: none;
	border: none;
	color: var(--theme-color-2);
	cursor: pointer;
	font-size: 0.95rem;
	font-weight: 600;
	padding: 0;
	text-decoration: none;
	&:hover:not(:disabled) {
		text-decoration: underline;
	}
}

@media (max-width: 500px) {
  .auth-card {
    padding: 1.5rem;
  }

  .auth-card h1 {
    font-size: 1.5rem;
  }
}
</style>
